import * as Headless from '@headlessui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ProviderDetail } from '../../../../../js/api';
import { SourceTypeEnum } from '../../../../../js/api';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../../../components/dropdown';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { useRequireActingAsEntity } from '../../../hooks/useRequireActingAsEntity';

/** Assignment-create deep link, same shape the legacy profile and "book again" use. */
export const assignmentCreateUrlForProvider = (
  providerId: string,
  availabilityToken: string | null | undefined,
  returnUrl: string
) => {
  const tokenParam = availabilityToken ? `&token=${availabilityToken}` : '';
  return `/app/organization/assignments/create?provider=${providerId}${tokenParam}&returnUrl=${encodeURIComponent(returnUrl)}`;
};

export const legacyProfileUrl = (providerId: string) =>
  `/app/healthcare-entity/providers/${providerId}`;

export const djangoAdminUrl = (providerId: string) =>
  `/admin/providers/provider/${providerId}/change/`;

/**
 * Kebab menu of secondary provider actions: New Assignment (marketplace providers,
 * needs the create permission and an acting-as organization), the legacy profile
 * (still where documents are downloaded from) and the Django admin (admin flag).
 * Connect will join the list once the connect dialog is ported off Bootstrap.
 */
export function ProviderActionsMenu({
  provider,
  showAdminLink,
}: {
  provider: ProviderDetail;
  showAdminLink: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const canCreateAssignment = useHasPermission('create__assignments__assignment');
  const requireActingAs = useRequireActingAsEntity();
  // Org-sourced (1099) providers are booked by their organization, not through
  // the marketplace — same rule as the legacy profile page.
  const showNewAssignment =
    canCreateAssignment && provider.source_type !== SourceTypeEnum.ORGANIZATION;

  return (
    <Dropdown>
      <DropdownButton
        as="button"
        className="tw:text-zinc-400 tw:hover:text-zinc-600 tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-1"
        aria-label="Clinician actions"
        title="Actions"
        data-testid="provider-actions-menu"
      >
        <i className="fas fa-ellipsis-vertical" />
      </DropdownButton>
      <Headless.Portal>
        <DropdownMenu anchor="bottom end" className="tw:z-[70]">
          {showNewAssignment && (
            <DropdownItem
              data-testid="provider-action-new-assignment"
              onClick={() =>
                requireActingAs(() =>
                  navigate(
                    assignmentCreateUrlForProvider(
                      provider.id,
                      provider.availability_token,
                      location.pathname + location.search
                    )
                  )
                )
              }
            >
              <i data-slot="icon" className="fas fa-fw fa-calendar-plus" />
              New Assignment
            </DropdownItem>
          )}
          <DropdownItem
            data-testid="provider-action-legacy-profile"
            onClick={() => window.open(legacyProfileUrl(provider.id), '_blank', 'noopener')}
          >
            <i data-slot="icon" className="fas fa-fw fa-file-arrow-down" />
            Legacy profile
          </DropdownItem>
          {showAdminLink && (
            <DropdownItem
              data-testid="provider-action-django-admin"
              onClick={() => window.open(djangoAdminUrl(provider.id), '_blank', 'noopener')}
            >
              <i data-slot="icon" className="fas fa-fw fa-user-gear" />
              Open in Django admin
            </DropdownItem>
          )}
        </DropdownMenu>
      </Headless.Portal>
    </Dropdown>
  );
}
